import { useState, useEffect, useCallback } from "react";
import { HandHeart, Sparkles, Gift, RefreshCw, Check, X, Copy, Clock, TrendingUp, Plus, Trash2, Search, Anchor } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { Avatar, PanelLoading } from "../../components/SmartImage";
import { toast } from "sonner";

const STEP_UPS = [
  { key: "covenant", label: "Covenant", hint: "They affirm a pledge to receive it" },
  { key: "pay_forward", label: "Pay it forward", hint: "Invited to bless/refer one in turn" },
  { key: "grace", label: "Grace window", hint: "Free season, then convert if they engage" },
  { key: "contribution", label: "Contribution", hint: "Honor-based step they commit to" },
];

const STATUS_STYLE = {
  active: "text-emerald-400 border-emerald-400/40",
  offered: "text-gold border-borderGold",
  converted: "text-gold border-gold",
  lapsed: "text-textDim border-borderGold",
  revoked: "text-ruby border-ruby/40",
  passed_on: "text-emerald-400 border-emerald-400/40",
};

export function BlessingsTab() {
  const [ov, setOv] = useState(null);
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(() => api.get("/admin/blessings/overview").then((r) => { setOv(r.data); setCfg(r.data.capacity.config); }), []);
  useEffect(() => { load(); api.get("/admin/members").then((r) => setMembers(r.data.members)); }, [load]);

  const openModal = (member) => setModal({
    member: member || null, blessing_type: "full", duration_days: 30, delivery: "email",
    step_ups: [], covenant_text: "", contribution_pledge: "", message: "", exceed_capacity_ack: false, q: "",
  });

  const submit = async () => {
    if (!modal.member) return toast.error("Choose who to bless");
    try {
      const { data } = await api.post("/admin/blessings", {
        member_id: modal.member.id, blessing_type: modal.blessing_type, duration_days: Number(modal.duration_days),
        delivery: modal.delivery, step_ups: modal.step_ups,
        covenant_text: modal.covenant_text, contribution_pledge: modal.contribution_pledge,
        message: modal.message, exceed_capacity_ack: modal.exceed_capacity_ack,
      });
      if (data.status === "capacity_warning") {
        setModal({ ...modal, _warn: true, exceed_capacity_ack: true });
        toast.warning("Beyond your current increase — confirm to give from faith.");
        return;
      }
      toast.success(modal.delivery === "email" ? "Blessing sent" : "Blessing granted");
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create blessing"); }
  };

  const revoke = async (id) => { await api.post(`/admin/blessings/${id}/revoke`); toast.success("Revoked"); load(); };
  const passForward = async (id) => { await api.post(`/admin/blessings/${id}/pass-forward`); toast.success("Marked passed forward"); load(); };
  const copyLink = async (token) => {
    const url = `${window.location.origin}/blessing/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Invite link copied"); } catch { toast.error("Copy failed"); }
  };
  const saveCfg = async (reanchor = false) => {
    const { data } = await api.patch("/admin/blessings/config", { ...cfg, reanchor });
    setOv({ ...ov, capacity: data.capacity }); toast.success(reanchor ? "Pool re-anchored" : "Saved");
  };

  if (!ov) return <PanelLoading label="Reading the increase" />;
  const c = ov.capacity;
  const filtered = (modal?.q ? members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(modal.q.toLowerCase())) : members).slice(0, 40);

  return (
    <div data-testid="blessings-tab">
      <div className="overline mb-3">// KAIROS BLESSINGS — give at the pace of increase</div>

      {/* Increase Pool */}
      <div className="panel p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="overline mb-1">// Capacity</div>
            <div data-testid="capacity-available" className={`font-display text-5xl ${c.available > 0 ? "text-gold" : "text-textMuted"}`}>{Math.max(0, c.available)}</div>
            <div className="text-xs text-textDim mt-1">earned {c.earned} · given {c.consumed}{c.available < 0 ? ` · ${Math.abs(c.available)} from faith` : ""}</div>
          </div>
          <div>
            <div className="overline mb-1">// Paid members</div>
            <div className="font-display text-5xl text-cream">{c.paid_members}</div>
          </div>
          <div>
            <div className="overline mb-1">// MRR</div>
            <div className="font-display text-5xl text-cream">{fmt.money(c.mrr_cents)}</div>
          </div>
          <div>
            <div className="overline mb-1">// Rhythm</div>
            {c.resting
              ? <div className="text-gold flex items-center gap-2 mt-3"><Clock className="w-5 h-5"/> Resting · {c.cooldown_remaining_hours}h</div>
              : <div className="text-emerald-400 flex items-center gap-2 mt-3"><TrendingUp className="w-5 h-5"/> Open to give</div>}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4 mt-6 pt-6 border-t border-borderGold">
          <Cfg label="Members / unit" v={cfg?.members_per_unit} on={(v) => setCfg({ ...cfg, members_per_unit: v })} />
          <Cfg label="$ MRR / unit" v={cfg?.mrr_dollars_per_unit} on={(v) => setCfg({ ...cfg, mrr_dollars_per_unit: v })} />
          <Cfg label="Cooldown hrs" v={cfg?.cooldown_hours} on={(v) => setCfg({ ...cfg, cooldown_hours: v })} />
          <button onClick={() => saveCfg(false)} className="btn-ghost text-xs">Save pace</button>
          <button data-testid="reanchor-pool" onClick={() => saveCfg(true)} className="btn-ghost text-xs"><Anchor className="w-3 h-3"/> Re-anchor</button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="overline">// Counts: {ov.counts.active} active · {ov.counts.offered} offered · {ov.counts.converted} converted · {ov.counts.lapsed} lapsed</div>
        <button data-testid="open-bless-modal" onClick={() => openModal(null)} className="btn-gold text-xs"><Gift className="w-4 h-4"/> Bless someone</button>
      </div>

      {/* Kairos prompts */}
      {ov.prompts.length > 0 && (
        <div className="mb-8">
          <div className="overline mb-3 text-gold">// KAIROS PROMPTS — a moment may be here</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ov.prompts.map((p) => (
              <div key={p.member.id} data-testid={`prompt-${p.member.id}`} className="panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 border border-borderGold overflow-hidden bg-surface shrink-0"><Avatar src={p.member.avatar_url} name={p.member.name} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-cream text-sm truncate">{p.member.name || p.member.email}</div>
                  <div className="text-xs text-gold truncate">{p.reason}</div>
                </div>
                <button onClick={() => openModal(p.member)} className="btn-ghost text-xs !py-1 !px-2"><HandHeart className="w-3 h-3"/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="overline mb-3">// THE LEDGER</div>
      {ov.recent.length === 0 ? (
        <p className="text-textMuted">No blessings yet. When increase comes, give discreetly.</p>
      ) : (
        <div className="space-y-2">
          {ov.recent.map((b) => (
            <div key={b.id} data-testid={`blessing-${b.id}`} className="panel p-4 flex flex-wrap items-center gap-3">
              <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_STYLE[b.status] || "text-textDim border-borderGold"}`}>{b.status}</span>
              <div className="flex-1 min-w-0">
                <div className="text-cream text-sm truncate">{b.member_name || b.member_email}</div>
                <div className="text-xs text-textDim">{b.blessing_type} · {b.duration_days}d · {b.delivery}{b.exceeded_capacity ? " · from faith" : ""}{b.expires_at ? ` · until ${fmt.date(b.expires_at)}` : ""}</div>
              </div>
              {b.status === "offered" && <button onClick={() => copyLink(b.accept_token)} className="btn-ghost text-xs !py-1 !px-2"><Copy className="w-3 h-3"/></button>}
              {(b.step_ups || []).includes("pay_forward") && !b.pay_forward_done && b.status === "active" &&
                <button onClick={() => passForward(b.id)} className="btn-ghost text-xs !py-1 !px-2" title="Mark passed forward"><Check className="w-3 h-3"/></button>}
              {["active", "offered"].includes(b.status) && <button onClick={() => revoke(b.id)} className="btn-ghost text-xs !py-1 !px-2 text-ruby"><X className="w-3 h-3"/></button>}
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-void/80 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setModal(null)}>
          <div className="panel p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="bless-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="overline text-gold">// SET ASIDE A BLESSING</div>
              <button onClick={() => setModal(null)}><X className="w-5 h-5 text-textMuted hover:text-cream"/></button>
            </div>

            {!modal.member ? (
              <div>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-textDim absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="bless-member-search" autoFocus value={modal.q} onChange={(e) => setModal({ ...modal, q: e.target.value })} placeholder="Search a member…" className="!pl-9"/>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-borderGold border border-borderGold">
                  {filtered.map((m) => (
                    <button key={m.id} onClick={() => setModal({ ...modal, member: m })} className="w-full text-left px-3 py-2 hover:bg-surface flex items-center gap-3">
                      <div className="w-7 h-7 border border-borderGold overflow-hidden bg-surface shrink-0"><Avatar src={m.avatar_url} name={m.name}/></div>
                      <span className="text-sm text-cream truncate">{m.name || m.email}</span>
                      <span className="text-xs text-textDim ml-auto">{m.tier}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-borderGold">
                  <div className="w-9 h-9 border border-borderGold overflow-hidden bg-surface"><Avatar src={modal.member.avatar_url} name={modal.member.name}/></div>
                  <div className="text-cream text-sm">{modal.member.name || modal.member.email}</div>
                  <button onClick={() => setModal({ ...modal, member: null })} className="ml-auto text-xs text-textMuted hover:text-cream">change</button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="overline">// Tier</label>
                    <select value={modal.blessing_type} onChange={(e) => setModal({ ...modal, blessing_type: e.target.value })}>
                      <option value="full">Sovereign (full)</option><option value="foundational">Foundational</option>
                    </select></div>
                  <div><label className="overline">// Season (days)</label>
                    <input type="number" min="1" value={modal.duration_days} onChange={(e) => setModal({ ...modal, duration_days: e.target.value })}/></div>
                </div>

                <div><label className="overline">// Delivery</label>
                  <div className="flex gap-2 mt-1">
                    {["email", "silent"].map((d) => (
                      <button key={d} onClick={() => setModal({ ...modal, delivery: d })} className={`flex-1 py-2 text-xs font-mono uppercase tracking-widest border ${modal.delivery === d ? "border-gold text-gold" : "border-borderGold text-textMuted"}`}>{d === "email" ? "Private invite" : "Silent grant"}</button>
                    ))}
                  </div>
                </div>

                <div><label className="overline">// The step up</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {STEP_UPS.map((s) => {
                      const on = modal.step_ups.includes(s.key);
                      return <button key={s.key} title={s.hint} onClick={() => setModal({ ...modal, step_ups: on ? modal.step_ups.filter((x) => x !== s.key) : [...modal.step_ups, s.key] })}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${on ? "border-gold text-gold" : "border-borderGold text-textMuted"}`}>{s.label}</button>;
                    })}
                  </div>
                </div>

                {modal.step_ups.includes("covenant") && (
                  <div><label className="overline">// Covenant (they affirm this)</label>
                    <textarea rows={2} value={modal.covenant_text} onChange={(e) => setModal({ ...modal, covenant_text: e.target.value })} placeholder="I receive this and I will show up…"/></div>
                )}
                {modal.step_ups.includes("contribution") && (
                  <div><label className="overline">// Contribution (honor-based)</label>
                    <input value={modal.contribution_pledge} onChange={(e) => setModal({ ...modal, contribution_pledge: e.target.value })} placeholder="What they commit to give / do"/></div>
                )}

                <div><label className="overline">// A private word</label>
                  <textarea rows={3} value={modal.message} onChange={(e) => setModal({ ...modal, message: e.target.value })} placeholder="A sanctified, personal note to the recipient…"/></div>

                {modal._warn && (
                  <div className="border border-gold/50 bg-gold/5 p-3 text-xs text-gold" data-testid="capacity-warning">
                    This is beyond your current increase. Press again to give from faith.
                  </div>
                )}
                <button data-testid="bless-submit" onClick={submit} className="btn-gold w-full"><Sparkles className="w-4 h-4"/> {modal.delivery === "email" ? "Send the blessing" : "Grant the blessing"}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Cfg({ label, v, on }) {
  return (
    <div>
      <label className="overline block mb-1">// {label}</label>
      <input type="number" min="1" value={v ?? ""} onChange={(e) => on(Number(e.target.value))} className="!w-24 text-sm"/>
    </div>
  );
}

// ============================================================
//  GLOBAL SERVICE BOARD
// ============================================================
export function ServiceBoardTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/service-board").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const done = async (id) => { await api.patch(`/admin/service-notes/${id}`, { status: "done" }); toast.success("Served. Well done."); load(); };
  const del = async (id) => { await api.delete(`/admin/service-notes/${id}`); load(); };

  if (!data) return <PanelLoading label="Gathering acts of service" />;
  return (
    <div data-testid="service-board">
      <div className="overline mb-3">// ACTS OF SERVICE — {data.open_count} open · {data.overdue_count} overdue · {data.done_count} served</div>
      {data.open.length === 0 ? (
        <p className="text-textMuted">Nothing open. Add service notes from any member's card.</p>
      ) : (
        <div className="space-y-2">
          {data.open.map((n) => (
            <div key={n.id} data-testid={`service-${n.id}`} className={`panel p-4 flex items-start gap-3 ${n.overdue ? "border-l-2 border-l-ruby" : ""}`}>
              <div className="flex-1 min-w-0">
                <div className="text-cream text-sm">{n.body}</div>
                <div className="text-xs text-textDim mt-1">
                  for <span className="text-gold">{n.member_name || n.member_email}</span>
                  {n.serve_by && <span className={n.overdue ? "text-ruby ml-2" : "ml-2"}>· serve by {fmt.date(n.serve_by)}{n.overdue ? " (overdue)" : ""}</span>}
                </div>
              </div>
              <button onClick={() => done(n.id)} className="btn-ghost text-xs !py-1 !px-2" title="Mark served"><Check className="w-3 h-3"/></button>
              <button onClick={() => del(n.id)} className="btn-ghost text-xs !py-1 !px-2 text-ruby" title="Delete"><Trash2 className="w-3 h-3"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
//  PER-MEMBER SERVICE NOTES (embedded in the Members edit panel)
// ============================================================
export function MemberServiceNotes({ memberId }) {
  const [notes, setNotes] = useState(null);
  const [body, setBody] = useState("");
  const [serveBy, setServeBy] = useState("");
  const load = useCallback(() => api.get(`/admin/members/${memberId}/service-notes`).then((r) => setNotes(r.data.notes)), [memberId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!body.trim()) return;
    await api.post(`/admin/members/${memberId}/service-notes`, { body, serve_by: serveBy || null });
    setBody(""); setServeBy(""); load();
  };
  const toggle = async (n) => { await api.patch(`/admin/service-notes/${n.id}`, { status: n.status === "done" ? "open" : "done" }); load(); };
  const del = async (id) => { await api.delete(`/admin/service-notes/${id}`); load(); };

  return (
    <div className="mt-4 pt-4 border-t border-borderGold" data-testid="member-service-notes">
      <label className="overline">// SERVICE NOTES — how can I serve them?</label>
      <div className="flex flex-wrap gap-2 mt-2">
        <input data-testid="service-note-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="An act of service for them…" className="flex-1 min-w-[200px]"/>
        <input type="date" value={serveBy} onChange={(e) => setServeBy(e.target.value)} className="!w-auto text-xs" title="Serve by (optional)"/>
        <button data-testid="service-note-add" onClick={add} className="btn-ghost text-xs"><Plus className="w-3 h-3"/> Add</button>
      </div>
      <div className="mt-3 space-y-2">
        {notes && notes.length === 0 && <p className="text-xs text-textDim">No service notes yet.</p>}
        {notes && notes.map((n) => (
          <div key={n.id} className="flex items-start gap-2 text-sm">
            <button onClick={() => toggle(n)} className={`mt-0.5 w-4 h-4 border shrink-0 flex items-center justify-center ${n.status === "done" ? "border-gold bg-gold/20" : "border-borderGold"}`}>
              {n.status === "done" && <Check className="w-3 h-3 text-gold"/>}
            </button>
            <div className="flex-1">
              <span className={n.status === "done" ? "text-textDim line-through" : "text-cream"}>{n.body}</span>
              {n.serve_by && <span className="text-xs text-textDim ml-2">· by {fmt.date(n.serve_by)}</span>}
            </div>
            <button onClick={() => del(n.id)} className="text-textDim hover:text-ruby"><Trash2 className="w-3 h-3"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
